import { requireService } from '@profgeosoft/di-ez';
import { Select } from '@repo/go-ui';
import { clsx } from 'clsx';
import { reaction } from 'mobx';
import { useCallback, useEffect, useMemo, useState, type MutableRefObject , ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ComboboxDropdown } from 'src/components/combobox-dropdown';
import { wrap } from 'src/packages/mobx-di/wrap';
import { hasValue } from 'src/packages/utils/has-value';

import type { RefSelectProps } from '@repo/go-ui';
import type { TOption } from 'src/packages/types';
import type { OptionsService } from 'src/services/directory-service/options-service';
import type { TComboBoxView, TFieldRulesData } from 'src/services/directory-service/types';

import styles from './combobox.module.scss';

type Props = {
  controlView: TComboBoxView;
  optionsService: OptionsService;
  className?: string;
  value: number | string | null;
  error?: string;
  fieldRulesData?: TFieldRulesData;
  innerRef?: MutableRefObject<RefSelectProps | null>;
  loadOptionsFn: (signal?: AbortSignal) => Promise<TOption[]>;
  onChange(fieldId: string, value: unknown): void;
  onBlur?(fieldId: string): void;
  onCreateNewRecord?(): void;
};

export const ComboboxComponent = wrap<Props>(function ComboboxComponent({
  innerRef,
  controlView,
  optionsService,
  className,
  value,
  error,
  fieldRulesData,
  onCreateNewRecord,
  onChange,
  loadOptionsFn,
  onBlur,
}) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const directoriesListService = requireService('directoriesListService');
  const [internalOptions, setInternalOptions] = useState<TOption<string | number>[]>(
    optionsService.options[controlView.fieldId] ?? [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const isEditable = useMemo(() => {
    return directoriesListService.getObjectType(controlView.refObjectType ?? '')?.isEditingAvailable ?? false;
  }, [directoriesListService, controlView.refObjectType]);

  const actualOption = hasValue(value) ? optionsService.optionsMap[controlView.fieldId].get(value) : null;

  const optionAsValue = (() => {
    if (actualOption) {
      return actualOption;
    }

    if (hasValue(value)) {
      return optionsService.archivedOptionsMap[controlView.fieldId]?.get(value) ?? null;
    }

    return null;
  })();

  const loadOptions = useCallback((): VoidFunction => {
    const abortController = new AbortController();

    const load = async () => {
      setIsLoading(true);
      const options = await loadOptionsFn(abortController.signal);
      setIsLoading(false);
      setInternalOptions(options);
    };

    load();

    return () => abortController.abort();
  }, [loadOptionsFn]);

  const handleChange = (value: number | string | null | TOption<string | number>) => {
    if (!hasValue(value)) {
      onChange(controlView.fieldId, null);
      return;
    }

    if (typeof value === 'number' || typeof value === 'string') {
      onChange(controlView.fieldId, value);
      return;
    }

    onChange(controlView.fieldId, value.value);
  };

  const handleBlur = () => {
    onBlur?.(controlView.fieldId);
  };

  useEffect(() => {
    if (!controlView.refQuery) {
      return;
    }

    return loadOptions();
  }, [controlView.refQuery, loadOptions]);

  useEffect(() => {
    return reaction(
      () => optionsService.options[controlView.fieldId] ?? [],
      (options) => setInternalOptions(options),
      { fireImmediately: true },
    );
  }, [controlView.fieldId, optionsService.options]);

  const dropDownRender = useMemo(() => {
    if (!onCreateNewRecord || !isEditable) {
      return undefined;
    }

    return function dr(menu: ReactElement) {
      return (
        <ComboboxDropdown onButtonClick={onCreateNewRecord} onClose={() => setIsOpen(false)}>
          {menu}
        </ComboboxDropdown>
      );
    };
  }, [isEditable, onCreateNewRecord]);

  const isOpened =
    isOpen &&
    !fieldRulesData?.isLoading &&
    !isLoading &&
    !fieldRulesData?.isDisabled &&
    !fieldRulesData?.isEnteringBlocked;

  return (
    <Select<TOption<string | number> | null>
      ref={innerRef}
      open={isOpened}
      value={optionAsValue}
      options={internalOptions}
      dropdownMatchSelectWidth={false}
      className={clsx(styles.combobox, className)}
      allowClear
      sortOptions
      loading={fieldRulesData?.isLoading || isLoading}
      status={error ? 'error' : undefined}
      optionFilterProp="label"
      showSearch
      disabled={fieldRulesData?.isDisabled || fieldRulesData?.isEnteringBlocked}
      placeholder={t('common:placeholders.chooseValue')}
      onDropdownVisibleChange={(open) => setIsOpen(open)}
      dropdownRender={dropDownRender}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
});
